import { Injectable, inject, DOCUMENT } from '@angular/core';

import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { SITE_NAME, SITE_URL, canonicalUrl } from '../site.config';
import { BreadcrumbItem } from '../hub/content-types';
import { DEFAULT_IMAGE_URL, JsonLdNode, absoluteAssetUrl, breadcrumbTrailLd, organizationLd } from './json-ld';

export interface SeoInput {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly indexable: boolean;
  readonly breadcrumb?: string | null;
  /// "Ana sayfa"dan sonraki halkalar; verilirse breadcrumb'ın yerine geçer.
  readonly breadcrumbTrail?: readonly BreadcrumbItem[];
  readonly ogType?: 'website' | 'article';
  /// 'assets/…' site yolu (1200×630); yoksa varsayılan paylaşım görseli.
  readonly image?: string | null;
  /// Özel görselin alt metni (og:image:alt); yoksa varsayılan "My PT uygulaması".
  readonly imageAlt?: string;
  /// /rehber/rss.xml için <link rel="alternate">.
  readonly rssFeed?: boolean;
  readonly jsonLd?: readonly JsonLdNode[];
}

export const JSON_LD_ELEMENT_ID = 'mypt-jsonld';
export const OG_IMAGE_URL = DEFAULT_IMAGE_URL;
export const RSS_URL = `${SITE_URL}/rehber/rss.xml`;

const DEFAULT_TITLE = 'My PT';

/// JSON içindeki "<" → <: içerik </script> kapatamaz.
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

/// Sayfa başına başlık/açıklama/canonical/OG/Twitter/JSON-LD. Prerender
/// sırasında head'e yazılır (statik HTML'de bulunur); tarayıcıda aynı
/// etiketleri bulup günceller, çiftlemez.
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  apply(input: SeoInput): void {
    const url = canonicalUrl(input.path);
    this.title.setTitle(input.title);
    this.setName('description', input.description);
    this.setName('robots', input.indexable ? 'index, follow' : 'noindex, follow');

    const image = input.image ? absoluteAssetUrl(input.image) : OG_IMAGE_URL;
    this.setProperty('og:type', input.ogType ?? 'website');
    this.setProperty('og:site_name', SITE_NAME);
    this.setProperty('og:locale', 'tr_TR');
    this.setProperty('og:title', input.title);
    this.setProperty('og:description', input.description);
    this.setProperty('og:url', url);
    this.setProperty('og:image', image);
    if (input.image) {
      this.meta.removeTag('property="og:image:width"');
      this.meta.removeTag('property="og:image:height"');
    } else {
      this.setProperty('og:image:width', '1200');
      this.setProperty('og:image:height', '630');
    }
    this.setProperty('og:image:alt', input.image && input.imageAlt ? input.imageAlt : 'My PT uygulaması');
    this.setName('twitter:card', 'summary_large_image');
    this.setName('twitter:title', input.title);
    this.setName('twitter:description', input.description);
    this.setName('twitter:image', image);

    if (input.indexable) {
      this.setCanonical(url);
    } else {
      this.removeCanonical();
    }

    if (input.rssFeed) {
      this.setRssLink();
    } else {
      this.removeRssLink();
    }

    const token = input.path === '/' ? environment.googleSiteVerification : '';
    if (token) {
      this.setName('google-site-verification', token);
    } else {
      this.meta.removeTag('name="google-site-verification"');
    }

    const trail = input.breadcrumbTrail ?? (input.breadcrumb ? [{ name: input.breadcrumb, path: input.path }] : null);
    const graph: JsonLdNode[] = [organizationLd(), ...(trail ? [breadcrumbTrailLd(trail)] : []), ...(input.jsonLd ?? [])];
    this.setJsonLd({ '@context': 'https://schema.org', '@graph': graph });
  }

  /// Site dışına (uygulamaya) geçince: uygulama sayfaları dizine alınmaz.
  reset(): void {
    this.title.setTitle(DEFAULT_TITLE);
    this.setName('robots', 'noindex');
    this.removeCanonical();
    this.removeRssLink();
    const el = this.doc.getElementById(JSON_LD_ELEMENT_ID);
    el?.parentNode?.removeChild(el);
  }

  private setName(name: string, content: string): void {
    this.meta.updateTag({ name, content }, `name="${name}"`);
  }

  private setProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content }, `property="${property}"`);
  }

  private setCanonical(href: string): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private removeCanonical(): void {
    const link = this.doc.head.querySelector('link[rel="canonical"]');
    link?.parentNode?.removeChild(link);
  }

  private setRssLink(): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="alternate"][type="application/rss+xml"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('type', 'application/rss+xml');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('title', 'My PT Rehber');
    link.setAttribute('href', RSS_URL);
  }

  private removeRssLink(): void {
    const link = this.doc.head.querySelector('link[rel="alternate"][type="application/rss+xml"]');
    link?.parentNode?.removeChild(link);
  }

  private setJsonLd(data: JsonLdNode): void {
    let el = this.doc.getElementById(JSON_LD_ELEMENT_ID);
    if (!el) {
      el = this.doc.createElement('script');
      el.setAttribute('type', 'application/ld+json');
      el.setAttribute('id', JSON_LD_ELEMENT_ID);
      this.doc.head.appendChild(el);
    }
    el.textContent = serializeJsonLd(data);
  }
}
